/**
 * AdminRouteGate v1.0 (2026-09-15) — the render decision for a superadmin-only route,
 * with no data access of its own, so it can be rendered and checked in isolation
 * (scripts/admin-route-guard-proof.mjs).
 *
 * ProtectedAdminRoute feeds it from useSuperadmin(), which asks
 * public.caller_is_superadmin(). This is the BROWSER half only. It stops a signed-in
 * non-admin from seeing the screen; it protects no data. Every RPC, table and function
 * an admin screen reads must refuse non-superadmin callers on the server as well.
 */
import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

export interface AdminRouteGateProps {
  isSuperadmin: boolean;
  loading: boolean;
  children: ReactNode;
  redirectTo?: string;
}

export function AdminRouteGate({ isSuperadmin, loading, children, redirectTo = "/dashboard" }: AdminRouteGateProps) {
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-admin-gate="checking">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Checking permissions...</p>
        </div>
      </div>
    );
  }
  if (!isSuperadmin) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}

export default AdminRouteGate;
